import type { Icon } from '@examdojo/core/icon';
import katex from 'katex';

export type RelationshipType = 'linear' | 'quadratic' | 'exponential';

/** Direction for non-linear relationship types */
export type CurveDirection = 'positive' | 'negative';

export interface CorrelationGeneratorConfig {
  readonly relationshipType: RelationshipType;
  /** Pearson r for linear (-1 to +1), R² for non-linear (0 to 1) */
  readonly targetStrength: number;
  /** Direction for non-linear types */
  readonly curveDirection: CurveDirection;
  readonly sampleSize: number;
  readonly xLabel: string;
  readonly yLabel: string;
  readonly decimalPlaces: number;
  readonly seed: string;
}

export const DEFAULT_CORRELATION_CONFIG: CorrelationGeneratorConfig = {
  relationshipType: 'linear',
  targetStrength: 0.5,
  curveDirection: 'positive',
  sampleSize: 100,
  xLabel: '',
  yLabel: '',
  decimalPlaces: 1,
  seed: '',
};

/** Quick-select presets for Pearson r (linear) */
export const PEARSON_PRESETS = [-0.9, -0.5, 0, 0.5, 0.9] as const;

/** Quick-select presets for R² (non-linear) */
export const R_SQUARED_PRESETS = [0.2, 0.5, 0.7, 0.9, 1.0] as const;

export const SAMPLE_SIZE_PRESETS = [10, 20, 30, 50, 100] as const;
export const DECIMAL_PLACES_OPTIONS = [0, 1, 2, 3] as const;

/** Whether a relationship type uses R² instead of Pearson r */
export function usesRSquared(type: RelationshipType): boolean {
  return type === 'quadratic' || type === 'exponential';
}

/** Whether a relationship type shows a direction toggle */
export function hasDirection(type: RelationshipType): boolean {
  return type === 'quadratic' || type === 'exponential';
}

export interface RelationshipCardDef {
  readonly type: RelationshipType;
  readonly label: string;
  /** KaTeX-rendered HTML for the formula subtitle */
  readonly subtitleHtml: string;
}

function renderMath(latex: string): string {
  return katex.renderToString(latex, { throwOnError: false, displayMode: false });
}

export const RELATIONSHIP_CARDS: readonly RelationshipCardDef[] = [
  { type: 'linear', label: 'Linear', subtitleHtml: renderMath('y = ax + b') },
  { type: 'quadratic', label: 'Quadratic', subtitleHtml: renderMath('y = ax^2 + bx + c') },
  { type: 'exponential', label: 'Exponential', subtitleHtml: renderMath('y = a \\cdot b^x') },
];

export interface DirectionOption {
  readonly value: CurveDirection;
  readonly label: string;
  readonly icon: Icon;
}

export const DIRECTION_OPTIONS: Record<string, readonly DirectionOption[]> = {
  quadratic: [
    { value: 'positive', label: 'Concave up', icon: 'turn-up' },
    { value: 'negative', label: 'Concave down', icon: 'turn-down' },
  ],
  exponential: [
    { value: 'positive', label: 'Growth', icon: 'arrow-trend-up' },
    { value: 'negative', label: 'Decay', icon: 'arrow-trend-down' },
  ],
};
